import { binarise } from './bitmap';
import { ADYEN_PAPER_WIDTH_DOTS, LayoutLine, linesHeight } from './layout';

/**
 * Rasteriser for grid-based prints on Adyen terminals: bongs, reports, test prints and any
 * other payload that arrives as fixed-width rows. Receipts use receipt.ts instead.
 *
 * Every character is drawn in its own fixed-width cell (see layout.ts), so the output stays
 * column-aligned regardless of which monospace font the device resolves. Strokes are drawn
 * with an outline as well as a fill: the monospace fallback on Adyen terminals is a thin
 * typewriter face that otherwise prints faintly.
 */

/** Render at this multiple of the paper resolution before downsampling to 1-bit. */
const SUPERSAMPLE = 2;

/** Vertical gap between a logo and the first text line. */
const LOGO_GAP_DOTS = 12;

/** Glyph advance to font-size ratio for typical monospace fonts; only a starting guess. */
const MONOSPACE_ADVANCE_RATIO = 0.6;

/** Fraction of the cell height that the font size should occupy. */
const FONT_HEIGHT_RATIO = 1 / 1.2;

/** Outline width (in paper dots) added around glyphs to thicken thin fallback fonts. */
const STROKE_DOTS = { normal: 0.6, bold: 1.1 };

/** Monospace stacks for iOS (Menlo), Android/Adyen terminals (Roboto/Droid) and fallbacks. */
const FONT_STACK =
  '"Menlo", "Roboto Mono", "Droid Sans Mono", "DejaVu Sans Mono", "Courier New", monospace';

export interface RenderOptions {
  /** Lines from layoutRows(), already segmented. */
  lines: LayoutLine[];
  /** Optional logo as a PNG data URL, already scaled to the paper width. Drawn above the text. */
  logoDataUrl?: string;
}

/** Bare base64 PNG (no data: prefix), which is what the terminal API expects. */
export type Base64Png = string;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load receipt logo'));
    img.src = src;
  });

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2d canvas context');
  }

  return { canvas, ctx };
};

/**
 * Finds the font size (in supersampled px) at which the glyph advance of the current
 * monospace font equals the target cell width. Two correction passes are enough because
 * advance scales linearly with font size.
 */
const fitFontSize = (
  ctx: CanvasRenderingContext2D,
  targetAdvance: number,
  weight: number,
  cache: Map<string, number>,
) => {
  const key = `${weight}:${targetAdvance}`;
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  let size = targetAdvance / MONOSPACE_ADVANCE_RATIO;

  for (let pass = 0; pass < 2; pass++) {
    ctx.font = `${weight} ${size}px ${FONT_STACK}`;
    const measured = ctx.measureText('M').width || size * MONOSPACE_ADVANCE_RATIO;
    size = (size * targetAdvance) / measured;
  }

  cache.set(key, size);
  return size;
};

/** Draws the lines onto a supersampled canvas and returns a 1-bit ImageData at paper resolution. */
const rasteriseText = (lines: LayoutLine[]): ImageData => {
  const height = linesHeight(lines);
  const ssWidth = ADYEN_PAPER_WIDTH_DOTS * SUPERSAMPLE;
  const ssHeight = height * SUPERSAMPLE;

  const { ctx } = createCanvas(ssWidth, ssHeight);
  const fontCache = new Map<string, number>();

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, ssWidth, ssHeight);
  ctx.fillStyle = '#000000';
  ctx.strokeStyle = '#000000';
  ctx.lineJoin = 'round';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  let y = 0;

  lines.forEach((line) => {
    const cellW = line.cellWidth * SUPERSAMPLE;
    const cellH = line.cellHeight * SUPERSAMPLE;
    const weight = line.bold ? 700 : 400;

    // Fit the glyph width to the cell, then stretch vertically so the glyph fills the
    // cell height (this is what turns a 12x24 cell into a 12x48 double-height one).
    const fontSize = fitFontSize(ctx, cellW, weight, fontCache);
    const naturalHeight = fontSize / FONT_HEIGHT_RATIO;
    const scaleY = cellH / naturalHeight;

    ctx.save();
    ctx.translate(0, y);
    ctx.scale(1, scaleY);
    ctx.font = `${weight} ${fontSize}px ${FONT_STACK}`;
    ctx.lineWidth = (line.bold ? STROKE_DOTS.bold : STROKE_DOTS.normal) * SUPERSAMPLE;

    const middle = cellH / scaleY / 2;

    for (let i = 0; i < line.text.length; i++) {
      const char = line.text[i];
      if (char !== ' ') {
        const x = i * cellW + cellW / 2;
        ctx.strokeText(char, x, middle);
        ctx.fillText(char, x, middle);
      }
    }

    ctx.restore();
    y += cellH;
  });

  const source = ctx.getImageData(0, 0, ssWidth, ssHeight);
  const { ctx: outCtx } = createCanvas(ADYEN_PAPER_WIDTH_DOTS, height);
  const target = outCtx.createImageData(ADYEN_PAPER_WIDTH_DOTS, height);

  return binarise({ source, supersample: SUPERSAMPLE, target });
};

/**
 * Renders one segment (optionally with a logo on top) to a bare base64 PNG.
 * The logo is copied through untouched so photographic logos keep their shading; only the
 * text region is binarised.
 */
export const renderSegment = async ({ lines, logoDataUrl }: RenderOptions): Promise<Base64Png> => {
  const logo = logoDataUrl ? await loadImage(logoDataUrl) : undefined;
  const logoHeight = logo ? Math.round((logo.height * ADYEN_PAPER_WIDTH_DOTS) / logo.width) : 0;
  const logoBlock = logo ? logoHeight + LOGO_GAP_DOTS : 0;

  const text = lines.length ? rasteriseText(lines) : undefined;
  const textHeight = text ? text.height : 0;

  const { canvas, ctx } = createCanvas(ADYEN_PAPER_WIDTH_DOTS, logoBlock + textHeight);

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (logo) {
    ctx.drawImage(logo, 0, 0, ADYEN_PAPER_WIDTH_DOTS, logoHeight);
  }

  if (text) {
    ctx.putImageData(text, 0, logoBlock);
  }

  return canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
};
